import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IPaginationResultFrequencies,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "app/services/abstract.service";
import { IFilters } from "../../filters/interfaces/filterInterface";
import { QuotaAttributes } from "@albatrosdeveloper/ave-models-package";

const apiUrl = env.API_QUOTA_URL;
const moduleName = "quota";

@Injectable({
  providedIn: "root",
})
export class SubRouteQuotaService extends AbstractService<
  QuotaAttributes,
  QuotaAttributes,
  QuotaAttributes
> {
  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  findAllWithCount(
    options: IPagination<QuotaAttributes>,
    dataToFilter?: IFilters
  ): Observable<IPaginationResultFrequencies<QuotaAttributes>> {
    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _id, filtro de fecha, ruta.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<QuotaAttributes>[] => {
      const baseFilter: FilterFn<QuotaAttributes> = params.useOrWhere
      ? orWhere("_id", Ops.not(null))
      : where("_id", Ops.not(null))

      return [
        baseFilter,
        dataToFilter?.startDate && dataToFilter?.endDate
        ? andWhere("_date" as keyof QuotaAttributes, Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate))
        : noop(),
        dataToFilter?.subRouteId && dataToFilter.subRouteId.length > 0
          ? andWhere("subRoutesIds" as keyof QuotaAttributes, Ops.in(...dataToFilter.subRouteId))
          : noop(),
      ];
    };

    // ========== BLOQUES DE BÚSQUEDA ==========

    // Objeto 1: businessPartnerCode
    const searchByCodeQueryObject: FilterFn<QuotaAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),
      hasSearch ? andWhere("businessPartnerCode", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: businessPartnerName
    const searchByNameQueryObject: FilterFn<QuotaAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("businessPartnerName", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 3: subRoutesCodes
    const searchBySubRouteCodeQueryObject: FilterFn<QuotaAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("subRoutesCodes", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 4: subRoutesNames
    const searchBySubRouteNameQueryObject: FilterFn<QuotaAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("subRoutesNames", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 5: materialGroupCode
    const searchByMaterialCodeQueryObject: FilterFn<QuotaAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("materialGroupCode", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 6: materialGroupName
    const searchByMaterialNameQueryObject: FilterFn<QuotaAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("materialGroupName", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 7: materialSubGroupCode
    const searchByMaterialSubCodeQueryObject: FilterFn<QuotaAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("materialSubGroupCode", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 8: materialSubGroupName
    const searchByMaterialSubCodeNameQueryObject: FilterFn<QuotaAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("materialSubGroupName", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 9: quota
    const searchByQuotaQueryObject: FilterFn<QuotaAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("quota", Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========
    const query = buildQuery<QuotaAttributes>(
      ...searchByCodeQueryObject,
      ...searchByNameQueryObject,
      ...searchBySubRouteCodeQueryObject,
      ...searchBySubRouteNameQueryObject,
      ...searchByMaterialCodeQueryObject,
      ...searchByMaterialNameQueryObject,
      ...searchByMaterialSubCodeQueryObject,
      ...searchByMaterialSubCodeNameQueryObject,
      ...searchByQuotaQueryObject,

      orderBy(
        options.sort ?? "businessPartnerCode",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResultFrequencies<QuotaAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(): Observable<IRequestResult<QuotaAttributes[]>> {
    const query = buildQuery<QuotaAttributes>(
      //where("statusId", StatusEnum.ACTIVE),
      orderBy("businessPartnerCode", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<QuotaAttributes[]>>(API_URL);
  }

  getFileTemplateQuota(): string {
    return env.QUOTA_LOAD_TEMPLATE || null;
  }

  importFile(data: FormData) {
    const API_URL: string = apiUrl + `api/${moduleName}/upload`;
    return this.http.post(API_URL, data);
  }
}